import { BotIcon } from "lucide-react";

import { Column, EmptyState, ErrorText, LoadingIndicator } from "@/components";
import { formatMinutes, relative } from "@/components/tools/assistant/format";
import { Chip, StatCard } from "@/components/tools/assistant/shared";
import {
  ITEM_STATUS,
  MESSAGE_KIND,
  MESSAGE_STATUS,
  ORIGIN,
  pushCount,
} from "@/components/tools/assistant/vocab";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { useGetAssistantOverview } from "@/hooks/queries/useGetAssistantAdmin";
import { AssistantRunLog } from "@/types";

// The scan runs hourly and the tick every 15 minutes; past these, something
// is wrong with the scheduler or the job.
const SCAN_LATE_MS = 2 * 60 * 60 * 1000;
const TICK_LATE_MS = 40 * 60 * 1000;

const runTone = (run: AssistantRunLog | null, lateMs: number) => {
  if (!run) return "amber" as const;
  if (run.error) return "red" as const;
  if (Date.now() - new Date(run.startedAt).getTime() > lateMs)
    return "amber" as const;
  return "green" as const;
};

// " · 2/3 quiet · 1/1 news": sent/due across Staffers for triggers 2 and 3b.
const scanTriggerSummary = (run: AssistantRunLog): string => {
  const rows = run.stats?.perStaffer ?? [];
  const sum = (pick: (p: (typeof rows)[number]) => number | undefined) =>
    rows.reduce((n, p) => n + (pick(p) ?? 0), 0);
  const part = (due: number, sent: number, label: string) =>
    due > 0 ? ` · ${sent.toString()}/${due.toString()} ${label}` : "";
  return (
    part(sum((p) => p.quietDue), sum((p) => p.quietSent), "quiet") +
    part(sum((p) => p.newsDue), sum((p) => p.newsSent), "news")
  );
};

export const AssistantOverviewTab = ({
  days,
  onOpenThread,
}: {
  days: number;
  onOpenThread: (threadId: string) => void;
}) => {
  const { data, status } = useGetAssistantOverview({ days });

  if (status === "pending") return <LoadingIndicator variant="medium" />;
  if (status === "error")
    return <ErrorText errorMessage="Error loading the assistant overview." />;

  const { emailScan, scheduleTick } = data.lastRuns;
  const totals = data.threads.reduce(
    (acc, t) => ({
      turns: acc.turns + t.chatTurns + t.voiceTurns,
      proactive: acc.proactive + pushCount(t),
      held: acc.held + t.proactive.heldForChat,
      open: acc.open + t.items.open,
      failed: acc.failed + t.failedReplies,
    }),
    { turns: 0, proactive: 0, held: 0, open: 0, failed: 0 },
  );

  return (
    <Column className="gap-6 overflow-auto pb-6">
      <div className="flex flex-wrap gap-3">
        <StatCard
          hint={
            emailScan
              ? `${emailScan.stats?.flagged?.toString() ?? "0"} flagged${scanTriggerSummary(emailScan)} · ${(
                  (emailScan.durationMs ?? 0) / 1000
                ).toFixed(0)}s${emailScan.error ? ` · ${emailScan.error}` : ""}`
              : "No run logged yet"
          }
          label="Last email scan"
          tone={runTone(emailScan, SCAN_LATE_MS)}
          value={relative(emailScan?.startedAt)}
        />
        <StatCard
          hint={
            scheduleTick
              ? `${scheduleTick.stats?.claimed?.toString() ?? "0"} claimed${
                  scheduleTick.error ? ` · ${scheduleTick.error}` : ""
                }`
              : "No tick logged yet"
          }
          label="Last schedule tick"
          tone={runTone(scheduleTick, TICK_LATE_MS)}
          value={relative(scheduleTick?.startedAt)}
        />
        <StatCard
          hint={`last ${data.days.toString()}d`}
          label="Staffer turns"
          tone="blue"
          value={totals.turns}
        />
        <StatCard
          hint={`${totals.held.toString()} held for chat`}
          label="Proactive messages"
          tone="violet"
          value={totals.proactive}
        />
        <StatCard
          label="Open emails"
          tone={totals.open ? "amber" : "slate"}
          value={totals.open}
        />
        <StatCard
          label="Failed replies"
          tone={totals.failed ? "red" : "slate"}
          value={totals.failed}
        />
      </div>

      {data.threads.length === 0 ? (
        <EmptyState
          description="Threads appear once a Staffer chats with the assistant or it reaches out."
          Icon={BotIcon}
          title="No assistant threads yet"
        />
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Staffer</TableHead>
              <TableHead>Talking to Phoebe</TableHead>
              <TableHead>Phoebe reached out</TableHead>
              <TableHead>Emails surfaced</TableHead>
              <TableHead className="text-right">Median resolve</TableHead>
              <TableHead className="text-right">Upcoming</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.threads.map((t) => (
              <TableRow
                className="cursor-pointer align-top"
                key={t.threadId}
                onClick={() => {
                  onOpenThread(t.threadId);
                }}
              >
                <TableCell>
                  <div className="flex flex-col">
                    <span className="font-medium text-slate-900">
                      {t.staffer.name}
                    </span>
                    <span className="text-xs text-slate-500">
                      {t.isOwn ? "" : `viewed by ${t.user.name} · `}
                      last message {relative(t.lastMessageAt)}
                    </span>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1">
                    <Chip count={t.chatTurns} def={MESSAGE_KIND.staffer_text} />
                    <Chip count={t.voiceTurns} def={MESSAGE_KIND.staffer_voice} />
                    <Chip count={t.assistantReplies} def={MESSAGE_KIND.reply} />
                    {t.failedReplies > 0 && (
                      <Chip count={t.failedReplies} def={MESSAGE_STATUS.FAILED} />
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex max-w-[360px] flex-wrap gap-1">
                    <Chip count={t.proactive.nudge} def={MESSAGE_KIND.nudge} />
                    <Chip count={t.proactive.renudge} def={MESSAGE_KIND.renudge} />
                    <Chip count={t.proactive.digest} def={MESSAGE_KIND.digest} />
                    <Chip count={t.proactive.reminder} def={MESSAGE_KIND.reminder} />
                    <Chip
                      count={t.proactive.quietRoundup}
                      def={MESSAGE_KIND.quiet_customer}
                    />
                    {t.proactive.plan > 0 && (
                      <Chip count={t.proactive.plan} def={MESSAGE_KIND.plan} />
                    )}
                    <Chip
                      count={t.proactive.postInterview}
                      def={MESSAGE_KIND.post_interview}
                    />
                    <Chip
                      count={t.proactive.candidateNews}
                      def={MESSAGE_KIND.candidate_news}
                    />
                    <Chip count={t.proactive.briefing} def={MESSAGE_KIND.briefing} />
                    <Chip count={t.proactive.wrapup} def={MESSAGE_KIND.wrapup} />
                    {t.proactive.followUp > 0 && (
                      <Chip count={t.proactive.followUp} def={ORIGIN.follow_up} />
                    )}
                    {t.proactive.heldForChat > 0 && (
                      <Chip count={t.proactive.heldForChat} def={ORIGIN.held_for_chat} />
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex max-w-[360px] flex-wrap gap-1">
                    <Chip count={t.items.open} def={ITEM_STATUS.open} />
                    <Chip count={t.items.resolvedByReply} def={ITEM_STATUS.reply} />
                    <Chip count={t.items.resolvedByEvidence} def={ITEM_STATUS.evidence} />
                    <Chip count={t.items.dismissed} def={ITEM_STATUS.staffer} />
                  </div>
                </TableCell>
                <TableCell className="text-right">
                  {formatMinutes(t.items.medianResolveMinutes)}
                </TableCell>
                <TableCell className="text-right">
                  {t.activeSchedules}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Column>
  );
};
